import { ReplaceLinesRequest, UpdateLinesRequest } from 'service/apicataleg/useApiUser';
import { APIAuthClient } from 'service/apicataleg/api/APIHttpClient';
import { CartDTO, ProductQuantity, ProductTC } from 'service/apicataleg/types/ApiCatalegTypes';

type ProductQuantityFlat = ProductTC & { qtty: number };

type RemoveLineRequest = {
  idCart: number;
  linia: string;
  lang: string;
};

const prodQttyToLines = (products: Array<ProductQuantityFlat>): Array<ProductQuantity> => {
  const lines = products.map((p) => {
    const { qtty, ...prod } = p;
    return { product: prod, qtty };
  });
  return lines;
};

type GetCartQuery = {
  idCart: number;
  lang: string;
};

type RemoveLinesRequest = {
  idCart: number;
  linies: Array<string>;
  lang: string;
};

type AddProductsRequest = {
  idCart: number;
  products: Array<ProductQuantityFlat>;
  lang: string;
};

type ClearCartRequest = {
  idCart: number;
};

type SaveNotesRequest = {
  idCart: number;
  notes: string;
};

type SaveNotificationMailRequest = {
  idCart: number;
  email: string;
};

type SaveDeliveryDateRequest = {
  idCart: number;
  strDate: string;
};

type SaveSendMailRequest = {
  idCart: number;
  isSend: boolean;
};

type SaveDireEntregaRequest = {
  idCart: number;
  codi: number;
};

const CartCommonService = {
  async getCart({ idCart, lang = '' }: GetCartQuery): Promise<CartDTO | null> {
    try {
      const { data } = await APIAuthClient.get(`/cart/priced/${idCart}?lang=${lang}`);
      return data;
    } catch (error) {
      return null;
    }
  },

  async removeLine({ idCart, linia, lang = '' }: RemoveLineRequest): Promise<CartDTO | null> {
    try {
      const { data } = await APIAuthClient.delete(`/cart/${idCart}/lines/${linia}/?lang=${lang}`);
      return data;
    } catch (error) {
      return null;
    }
  },

  async removeLines({
    idCart,
    linies = [],
    lang = '',
  }: RemoveLinesRequest): Promise<CartDTO | null> {
    try {
      const { data } = await APIAuthClient.delete(`/cart/${idCart}/lines?lang=${lang}`, {
        data: linies,
      });
      return data;
    } catch (error) {
      return null;
    }
  },

  async addProducts({ products, idCart, lang = '' }: AddProductsRequest): Promise<CartDTO | null> {
    const lines = prodQttyToLines(products);
    try {
      const { data } = await APIAuthClient.post(`/cart/${idCart}?lang=${lang}`, {
        lines,
      });
      return data;
    } catch (error) {
      console.log(error);
      return null;
    }
  },

  async replace({ lines, idCart }: ReplaceLinesRequest): Promise<boolean> {
    try {
      const { status } = await APIAuthClient.put(`/cart/${idCart}/replace`, {
        lines,
      });
      return status >= 200 && status <= 299;
    } catch (error) {
      return false;
    }
  },

  async updateLines(req: UpdateLinesRequest): Promise<boolean> {
    try {
      await APIAuthClient.put('/cart/lines', req.lines);
      return true;
    } catch (error) {
      return false;
    }
  },

  async clearCart({ idCart }: ClearCartRequest): Promise<CartDTO | null> {
    try {
      const { data } = await APIAuthClient.post(`/cart/${idCart}/clear`);
      return data;
    } catch (error) {
      return null;
    }
  },

  async saveCartNotes({ idCart, notes }: SaveNotesRequest): Promise<boolean> {
    try {
      const { status } = await APIAuthClient.put(`/cart/${idCart}/cap/notes`, { str: notes });
      return status >= 200 && status <= 299;
    } catch (error) {
      return false;
    }
  },

  async saveCartNotificationEmail({
    idCart,
    email,
  }: SaveNotificationMailRequest): Promise<boolean> {
    try {
      const { status } = await APIAuthClient.put(`/cart/${idCart}/cap/email`, { str: email });
      return status >= 200 && status <= 299;
    } catch (error) {
      return false;
    }
  },

  async saveCartNotes2({ idCart, notes }: SaveNotesRequest): Promise<boolean> {
    try {
      const { status } = await APIAuthClient.put(`/cart/${idCart}/cap/notes2`, { str: notes });
      return status >= 200 && status <= 299;
    } catch (error) {
      return false;
    }
  },

  async saveCartAdditionalNotes({ idCart, notes }: SaveNotesRequest): Promise<boolean> {
    try {
      const { status } = await APIAuthClient.put(`/cart/${idCart}/cap/additional-notes`, {
        str: notes,
      });
      return status >= 200 && status <= 299;
    } catch (error) {
      return false;
    }
  },

  async saveDeliveryDate({ idCart, strDate }: SaveDeliveryDateRequest): Promise<boolean> {
    try {
      const { status } = await APIAuthClient.put(`/cart/${idCart}/cap/deliverydate`, {
        date: strDate,
      });
      return status >= 200 && status <= 299;
    } catch (error) {
      return false;
    }
  },

  async saveSendMail({ idCart, isSend }: SaveSendMailRequest): Promise<boolean> {
    try {
      await APIAuthClient.put(`/cart/${idCart}/cap/sendmail`, {
        bool: Boolean(isSend),
      });
      return true;
    } catch (error) {
      return false;
    }
  },

  async saveDirEntrega({ idCart, codi }: SaveDireEntregaRequest): Promise<boolean> {
    try {
      await APIAuthClient.put(`/cart/${idCart}/cap/direntrega/${codi}`);
      return true;
    } catch (error) {
      return false;
    }
  },

  async deleteCart(id: number): Promise<boolean> {
    try {
      await APIAuthClient.delete(`/cart/${id}`);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  },
};

export default CartCommonService;
